/**
 * Cookie Preferences Modal
 *
 * Detailed cookie preferences with toggles for each category.
 * Can be opened from banner or Settings page.
 */

import { useState, useEffect } from 'react';
import { useCookieConsent } from '../context/CookieConsentContext';

const CookiePreferencesModal = () => {
  const {
    showPreferences,
    closePreferences,
    consent,
    updatePreferences,
    acceptAll,
    policyVersion
  } = useCookieConsent();

  // Local state for toggles
  const [functional, setFunctional] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [saving, setSaving] = useState(false);

  // Sync with current consent when modal opens
  useEffect(() => {
    if (showPreferences && consent) {
      setFunctional(consent.functional ?? false);
      setAnalytics(consent.analytics ?? false);
    }
  }, [showPreferences, consent]);

  if (!showPreferences) return null;

  const handleSave = async () => {
    setSaving(true);
    await updatePreferences({
      essential: true,
      functional,
      analytics
    });
    setSaving(false);
  };

  const handleAcceptAll = async () => {
    setSaving(true);
    await acceptAll();
    setSaving(false);
  };

  // Toggle component
  const Toggle = ({ enabled, onChange, disabled = false }) => (
    <button
      type="button"
      onClick={() => !disabled && onChange(!enabled)}
      disabled={disabled}
      className={`
        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#06c] focus:ring-offset-2
        ${disabled ? 'cursor-not-allowed opacity-60' : ''}
        ${enabled ? 'bg-[#34c759]' : 'bg-[#e5e5ea] dark:bg-[#3a3a3c]'}
      `}
      aria-pressed={enabled}
    >
      <span
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0
          transition duration-200 ease-in-out
          ${enabled ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={closePreferences}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-[#1c1c1e] rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="px-6 py-5 border-b border-[#d2d2d7] dark:border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">
              Cookie Preferences
            </h2>
            <p className="text-xs text-[#6e6e73] dark:text-[#98989d] mt-1">
              Policy version: {policyVersion}
            </p>
          </div>
          <button
            onClick={closePreferences}
            className="p-2 hover:bg-[#f5f5f7] dark:hover:bg-[#2c2c2e] rounded-lg transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5 text-[#6e6e73]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
          {/* Intro */}
          <p className="text-[14px] text-[#6e6e73] dark:text-[#98989d] mb-6 leading-relaxed">
            Choose which cookies you want to accept. Essential cookies are always enabled as they are required for the app to function properly.
          </p>

          {/* Cookie Categories */}
          <div className="space-y-4">
            {/* Essential Cookies */}
            <div className="p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-[15px] font-semibold text-[#1d1d1f] dark:text-white">
                      Essential Cookies
                    </h3>
                    <span className="px-2 py-0.5 bg-[#34c759]/10 text-[#34c759] text-[11px] font-medium rounded-full">
                      Always On
                    </span>
                  </div>
                  <p className="text-[13px] text-[#6e6e73] dark:text-[#98989d] leading-relaxed">
                    Required for authentication, security, and core app functionality. These cannot be disabled.
                  </p>
                </div>
                <Toggle enabled={true} onChange={() => {}} disabled={true} />
              </div>
            </div>

            {/* Functional Cookies */}
            <div className="p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-[15px] font-semibold text-[#1d1d1f] dark:text-white mb-1">
                    Functional Cookies
                  </h3>
                  <p className="text-[13px] text-[#6e6e73] dark:text-[#98989d] leading-relaxed">
                    Enable personalization features like theme preferences, UI settings, and remembering your choices.
                  </p>
                </div>
                <Toggle enabled={functional} onChange={setFunctional} />
              </div>
            </div>

            {/* Analytics Cookies */}
            <div className="p-4 bg-[#f5f5f7] dark:bg-[#2c2c2e] rounded-xl">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <h3 className="text-[15px] font-semibold text-[#1d1d1f] dark:text-white mb-1">
                    Analytics Cookies
                  </h3>
                  <p className="text-[13px] text-[#6e6e73] dark:text-[#98989d] leading-relaxed">
                    Help us understand how you use QuickMeet so we can improve our service. We use privacy-friendly analytics.
                  </p>
                </div>
                <Toggle enabled={analytics} onChange={setAnalytics} />
              </div>
            </div>
          </div>

          {/* Privacy Link */}
          <p className="mt-6 text-[13px] text-[#6e6e73] dark:text-[#98989d]">
            For more information, read our{' '}
            <a
              href="/privacy"
              className="text-[#06c] hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy Policy
            </a>
            {' '}and{' '}
            <a
              href="/cookies"
              className="text-[#06c] hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Cookie Policy
            </a>
            .
          </p>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#d2d2d7] dark:border-white/10 flex flex-col sm:flex-row gap-3 bg-[#fafafa] dark:bg-[#1c1c1e]">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-5 py-3 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[14px] font-medium rounded-xl hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
          <button
            onClick={handleAcceptAll}
            disabled={saving}
            className="flex-1 px-5 py-3 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white text-[14px] font-semibold rounded-xl hover:shadow-lg hover:shadow-[#06c]/25 transition-all disabled:opacity-50"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

export default CookiePreferencesModal;
